import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Clock, Dumbbell } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell } from "../components/Shell";
import { Avatar } from "../components/Avatar";
import { PlanShare } from "../components/PlanShare";
import { api } from "../lib/api";
import { formatTime } from "../constants/rox";
import { useT } from "../i18n";

export default function Plan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [plan, setPlan] = useState(null);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    api.get(`/matches/${id}/plan`, { params: { lang } }).then((r) => setPlan(r.data)).catch(() => navigate("/matchs"));
  }, [id, navigate, lang]);

  const toggle = async (i) => {
    setToggling(i);
    try {
      const { data } = await api.post(`/matches/${id}/plan/sessions/${i}/toggle`);
      setPlan((p) => ({ ...p, done: data.done }));
    } finally {
      setToggling(null);
    }
  };

  if (!plan) return <AppShell><TopBar /><p className="p-5 text-sm text-rox-muted">{t("plan_loading")}</p></AppShell>;
  const { me, partner } = plan;
  const doneCount = plan.done.filter(Boolean).length;

  return (
    <AppShell>
      <TopBar onBack={() => navigate(`/match/${id}`)} />
      <main className="print-page px-5 pb-16 pt-2 stagger lg:mx-auto lg:max-w-3xl" data-testid="plan-page">
        <p className="print-brand font-display text-2xl uppercase">RO<span className="text-rox-orange">X</span>PAIR · {t("plan_brand")}</p>
        <p className="eyebrow text-rox-orange">{t("plan_brand")}</p>
        <h1 className="h-display mt-1 text-4xl">{t("plan_h1a")} <span className="text-rox-orange">{t("plan_h1b")}</span></h1>
        <p className="mt-3 text-sm text-rox-off/80" data-testid="plan-intro">{plan.intro}</p>

        <div className="panel mt-5 flex items-center gap-4 p-4">
          <Avatar name={me.first_name} src={me.photo_url} size={48} ring={false} />
          <span className="font-display text-xl text-rox-orange">X</span>
          <Avatar name={partner.first_name} src={partner.photo_url} size={48} ring={false} />
          <div className="ml-auto text-right">
            <p className="eyebrow">{t("plan_common_time")}</p>
            <p className="font-display text-2xl">{formatTime(plan.target_time)}</p>
          </div>
        </div>

        <div className="panel mt-4 p-4" data-testid="plan-progress">
          <div className="flex items-center justify-between">
            <p className="eyebrow">{t("plan_progress")}</p>
            <p className="font-display text-xl"><span className="text-rox-orange">{doneCount}</span>{t("plan_count", doneCount, plan.days_to_race).slice(String(doneCount).length)}</p>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            {plan.done.map((d, i) => <div key={i} className={`h-1.5 rounded-sm ${d ? "bg-rox-orange" : "bg-rox-gray"}`} />)}
          </div>
          {doneCount === 4 && <p className="mt-3 text-sm text-rox-off/85" data-testid="plan-complete">{t("plan_done", plan.race_name)}</p>}
        </div>

        <ol className="mt-6 space-y-4" data-testid="plan-sessions">
          {plan.sessions.map((s, i) => {
            const done = plan.done[i];
            return (
            <li key={s.title} className={`panel p-4 transition-[border-color] ${done ? "border-rox-orange/60" : ""}`} data-testid={`plan-session-${i + 1}`} data-done={done}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggle(i)} disabled={toggling === i} data-testid={`plan-session-toggle-${i + 1}`} aria-label={done ? t("plan_mark_undone") : t("plan_mark_done")}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center font-display text-lg transition-[background-color,color] ${done ? "bg-rox-orange text-rox-black" : "border border-rox-line text-rox-off hover:border-rox-orange"}`}>
                  {done ? <Check size={18} strokeWidth={3} /> : i + 1}
                </button>
                <div className="min-w-0 flex-1">
                  <h2 className={`h-display text-2xl ${done ? "text-rox-off/60 line-through decoration-rox-orange decoration-2" : ""}`}>{s.title}</h2>
                  <p className="mt-1 text-sm text-rox-off/75">{s.focus}</p>
                </div>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-rox-muted"><Clock size={13} /> {s.duration}</p>
              <ul className="mt-3 space-y-2">
                {s.blocks.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm"><Dumbbell size={14} className="mt-0.5 shrink-0 text-rox-orange" /> {b}</li>
                ))}
              </ul>
            </li>
            );
          })}
        </ol>

        <h2 className="h-display mt-8 text-3xl">{t("plan_split_a")} <span className="text-rox-orange">{t("plan_split_b")}</span></h2>
        <p className="mt-1 text-xs text-rox-muted">{t("plan_split_sub")}</p>
        <div className="panel mt-4 divide-y divide-rox-line" data-testid="plan-split">
          {plan.split.map((s) => (
            <div key={s.station} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="eyebrow text-rox-off/80">{s.station}</span>
              <span className="font-display text-base uppercase">{s.lead} <span className="text-rox-orange">{s.share} %</span></span>
            </div>
          ))}
        </div>
        <PlanShare plan={plan} />
      </main>
    </AppShell>
  );
}
